import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import classNames from 'classnames';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AutocompleteTagsExclusionFilterInput from '../../../../../components/Filters/AutocompleteTagsExclusionFilterInput';
import AutocompleteTagsFilterInput from '../../../../../components/Filters/AutocompleteTagsFilterInput/AutocompleteTagsFilterInput';
import CompanyActivityFilterInput from '../../../../../components/Filters/CompanyActivityFilterInput';
import { FALLBACK_DEFAULT_COMPANY_ACTIVITY_FILTER_STATE } from '../../../../../components/Filters/CompanyActivityFilterInput/CompanyActivityFilterInput';
import CountryTagsExclusionFilterInput from '../../../../../components/Filters/CountryTagsExclusionFilterInput';
import CurrencyFilterInput from '../../../../../components/Filters/CurrencyFilterInput';
import DateFilterInput from '../../../../../components/Filters/DateFilterInput/DateFilterInput';
import DateRangeFilterInput from '../../../../../components/Filters/DateRangeFilterInput';
import NumberRangePickerWithYearPeriodFilterInput from '../../../../../components/Filters/NumberRangePickerWithYearPeriodFilterInput';
import SingleTagFilterInput from '../../../../../components/Filters/SingleTagFilterInput';
import StatusTagsExclusionFilterInput from '../../../../../components/Filters/StatusTagsExclusionFilterInput';
import SwitchShareholderFilterInput from '../../../../../components/Filters/SwitchShareholderFilterInput';
import SwitchShareholdingFilterInput from '../../../../../components/Filters/SwitchShareholdingFilterInput';
import SwitchWithNumberRangeFilterInput from '../../../../../components/Filters/SwitchWithNumberRangeFilterInput/SwitchWithNumberRangeFilterInput';
import TagsFilterInput from '../../../../../components/Filters/TagsFilterInput/TagsFilterInput';
import TagsWithExclusionFilterInput from '../../../../../components/Filters/TagsWithExclusionFilterInput';
import TextFieldFilterInput from '../../../../../components/Filters/TextFieldFilterInput';
import TreeWithExclusionFilterInput from '../../../../../components/Filters/TreeWithExclusionFilterInput';
import ValuesWithYearPeriodAndYearRangeFilterInput from '../../../../../components/Filters/ValuesWithYearPeriodAndYearRangeFilterInput/ValuesWithYearPeriodAndYearRangeFilterInput';
import YearRangeOpenEndedFilterInput from '../../../../../components/Filters/YearRangeOpenEndedFilterInput';
import YearRangePickerFilterInput from '../../../../../components/Filters/YearRangePickerFilterInput';
import YearRangeWithPeriodCriteriaFilterInput from '../../../../../components/Filters/YearRangeWithPeriodCriteriaFilter';
import { useClassifiersState } from '../../../../../contexts/ClassifiersContext/ClassifiersContext';
import { ClassifiersTypes } from '../../../../../contexts/ClassifiersContext/ClassifiersContext.types';
import { CONSTANTS } from '../../../../../core/config/constants';
import { FilterBackgrounds } from '../../../../../core/config/mainTheme';
import { CompanySearchFilters } from '../../../../../generated/model/models';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import {
  FilterInputType,
  FinancialDataContextActionTypes,
  LibraryFilterItem,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import { getFilterColor } from '../FilterLibrary/FilterDefinitions';

interface Props {
  labelKey: string;
}

const AppliedFiltersItem: React.FC<Props> = React.memo(({ labelKey }) => {
  const classes = useStyles();
  const dispatch = useFilterDispatch();
  const { data, activeFiltter, flattenedLibraryFilters, isRefinement } = useFilterState();
  const classifiers = useClassifiersState();
  const { t } = useTranslation();
  const filter = flattenedLibraryFilters[labelKey];

  const defaultValue = getDefaultState(filter.labelKey);

  const renderedItem = useMemo(() => {
    const allowClose =
      filter &&
      !filter.mandatory &&
      !(filter.type === FilterInputType.CurrencyFilter && isRefinement);
    return (
      filter && (
        <Box
          component="div"
          className={classes.filterBox}
          data-cy={`${filter.labelKey}`}
          style={{
            backgroundColor: getFilterColor(filter.parent!),
            borderLeft: `4px solid ${GetBorderColor(getFilterColor(filter.parent!))}`,
          }}
        >
          <div className={classNames(classes.filterTitleBox)}>
            <Typography
              className={classNames(classes.typography)}
              onClick={() => {
                dispatch({
                  type: FinancialDataContextActionTypes.ToggleActiveFilter,
                  payload: labelKey,
                });
              }}
            >
              <span>{t(`companies:filters.${filter.labelKey}`)}</span>
              <button className={classNames(classes.button)}>
                <EditIcon fontSize="small" />
              </button>
            </Typography>

            {allowClose && (
              <button
                className={classNames(classes.closeButton, classes.button)}
                onClick={() => {
                  dispatch({
                    type: FinancialDataContextActionTypes.Remove,
                    payload: labelKey,
                  });
                }}
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
          </div>
          <div
            className={classNames(classes.filterContent, {
              filterContentCollapsed: filter.labelKey !== activeFiltter,
            })}
          >
            {GetInputField(filter)}
          </div>
        </Box>
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, activeFiltter, defaultValue]);

  return <>{renderedItem}</>;

  function GetBorderColor(color: string | undefined) {
    if (!color) {
      return '';
    }
    switch (color) {
      case FilterBackgrounds.FirstChild:
        return FilterBackgrounds.FirstChildDark;
      case FilterBackgrounds.SecondChild:
        return FilterBackgrounds.SecondChildDark;
      case FilterBackgrounds.ThirdChild:
        return FilterBackgrounds.ThirdChildDark;
      default:
        // default return FilterBackgrounds.Ownership
        return FilterBackgrounds.FourthChildDark;
    }
  }

  function GetInputField(filter: LibraryFilterItem) {
    switch (filter.type) {
      case FilterInputType.DateRangeFilter:
        return (
          <DateRangeFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.WildcardTagsWithExclusionTagsFilter:
        return (
          <TagsWithExclusionFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            field={filter.labelKey}
            defaultValue={defaultValue}
            labelFormatter={filter.options?.labelFormatter}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.NumberRangeWithYearsFilter:
        return (
          <NumberRangePickerWithYearPeriodFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            additionalReadableStatement={[]}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
            isPercentage={filter.isPercentage}
          />
        );
      case FilterInputType.WildcardTagsFilter:
        return (
          <TagsFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            field={filter.labelKey}
            defaultValue={defaultValue}
            labelFormatter={filter.options?.labelFormatter}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.TextFilter:
        return (
          <TextFieldFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.YearRangeFilter:
        return (
          <YearRangePickerFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            field={filter.labelKey}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.YearRangeWithOpenEndsFilter:
        return (
          <YearRangeOpenEndedFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            field={filter.labelKey}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.SwitchShareholderFilter:
        return (
          <SwitchShareholderFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.SwitchShareholdingFilter:
        return (
          <SwitchShareholdingFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.SwitchWithNumberRangeFilter:
        return (
          <SwitchWithNumberRangeFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.ValuesWithYearPeriodAndYearRangeFilter:
        return (
          <ValuesWithYearPeriodAndYearRangeFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            items={getClassifier(filter.options?.classifier)}
            criteriaOptions={filter.options?.criteriaOptions}
            defaultCriteria={filter.options?.defaultCriteria!}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.DatePickerFilter:
        return (
          <DateFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={
              defaultValue ?? {
                value: format(new Date(), CONSTANTS.DATE_FORMATS.API),
              }
            }
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.ExactTagsWithExclusionTagsFilter:
        return (
          <AutocompleteTagsExclusionFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            defaultValue={defaultValue}
            items={getClassifier(filter.options?.classifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.StatusTagsExclusionFilterInput:
        return (
          <StatusTagsExclusionFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            items={getClassifier(filter.options?.classifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.CountryTagsExclusionFilterInput:
        return (
          <CountryTagsExclusionFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            items={getClassifier(filter.options?.classifier)}
            excludeItems={getClassifier(filter.options?.excludeClassifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.ExactTagsFilter:
      case FilterInputType.ContactInformationFilter:
        return (
          <AutocompleteTagsFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            withUnknownSwitch={filter.withUnknownSwitch}
            unknownSwitchEnabled={filter.unknownSwitchEnabled}
            defaultValue={defaultValue}
            items={getClassifier(filter.options?.classifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.TreeWithExclusionFilter:
        return (
          <TreeWithExclusionFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            defaultValue={defaultValue}
            items={getClassifier(filter.options?.classifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.YearRangeWithPeriodCriteriaFilter:
        return (
          <YearRangeWithPeriodCriteriaFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            criteriaOptions={filter.options?.criteriaOptions}
            defaultCriteria={filter.options?.defaultCriteria!}
            defaultValue={defaultValue}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.SingleTagFilter:
        return (
          <SingleTagFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            // session stored defaultValue has precedence over configured default value
            defaultValue={defaultValue || filter.defaultValue}
            items={getClassifier(filter.options?.classifier)}
            readableStatementLabelKey={filter.options?.readableStatementLabelKey}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.CurrencyFilter:
        return (
          <CurrencyFilterInput
            root={true}
            active={filter.labelKey === activeFiltter}
            // session stored defaultValue has precedence over configured default value
            defaultValue={defaultValue || filter.defaultValue}
            currencies={getClassifier(filter.options?.classifier)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      case FilterInputType.CompanyActivityFilter:
        return (
          <CompanyActivityFilterInput
            active={filter.labelKey === activeFiltter}
            // session stored defaultValue has precedence over configured default value
            defaultValue={
              defaultValue || filter.defaultValue || FALLBACK_DEFAULT_COMPANY_ACTIVITY_FILTER_STATE
            }
            itemsNace={getClassifier(filter.options?.classifierNace)}
            itemsSic={getClassifier(filter.options?.classifierSic)}
            onChange={(values) =>
              dispatch({
                type: FinancialDataContextActionTypes.UpdateFilterValues,
                payload: { [filter.labelKey]: values },
              })
            }
          />
        );
      default:
        // eslint-disable-next-line no-console
        console.log('No Input type defined: ', filter.type);
        return null;
    }
  }

  function getClassifier(classifier: ClassifiersTypes) {
    return classifiers[classifier] ?? [];
  }

  function getDefaultState(labelKey: string): any {
    return data[labelKey as keyof CompanySearchFilters];
  }
});

const useStyles = makeStyles((theme: Theme) => ({
  filterBox: {
    margin: '5px 0 10px',
    padding: `0 ${theme.spacing(4)}px`,
    '& .MuiInputBase-input': {
      minHeight: '18px',
      height: '18px',
    },
    '& .MuiInputBase-root': {
      minHeight: '32px',
    },
    '&.searchBlock': {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.palette.secondary.main,
      borderLeft: `${theme.spacing(2)}px solid ${theme.palette.secondary.dark}`,
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(6),
      paddingBottom: theme.spacing(2),
    },
  },
  filterContent: {
    paddingBottom: theme.spacing(5),

    '&.filterContentCollapsed': {
      paddingBottom: theme.spacing(0),
    },
  },
  typography: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${theme.spacing(3)}px 0`,
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.fontSize - 2,
    cursor: 'pointer',
    width: '100%',

    '&.typographyCollapsed': {
      marginBottom: 0,
      borderBottom: 0,
    },
  },
  closeButton: {
    marginRight: -1 * theme.spacing(2),
    marginLeft: 'auto',
  },
  filterTitleBox: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(6),

    '&.filterTitleBoxCollapsed': {
      marginBottom: 0,
      borderBottom: 0,
    },
  },
  button: {
    background: 'none',
    border: 0,
    cursor: 'pointer',
    color: theme.palette.secondary.light,
    padding: theme.spacing(0),
    display: 'contents',
  },
}));

export default AppliedFiltersItem;
